import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BORDER,
  BORDER_SOFT,
  BORDER_STRONG,
  BottomSheet,
  DESTRUCTIVE,
  Icon,
  Pill,
  SelectBox,
  TEAL,
  TEAL_DARK,
  TEXT,
  TEXT_DIM,
  TEXT_MUTED,
  t,
} from '../../components/list';
import type { ProductOption } from './CreateProductModifier';
import { inventoryFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from '../../services/fieldLimits';
import FieldCounter from '../../components/FieldCounter';
import { useDialogFocus, useFormKeyboard } from '../../hook/useFormKeyboard';

/**
 * Mobile (< 1024px) layout of the product modifier create / edit screen: one
 * scrolling page with three collapsible sections (Details, Options, Products),
 * an option sheet with a full-height product picker, and a sticky Cancel / Save
 * footer. The desktop wizard in CreateProductModifier.tsx is untouched; this
 * component only receives its state and handlers and never builds a payload.
 */

export interface MobileAnswerRow {
  id?: string;
  answer_text: string;
  product_id: string | null;
  color_code: string | null;
  display_order: number;
  max_answer?: number;
  is_active?: boolean;
  date?: Date | string;
}

export interface ModifierFormValues {
  name: string;
  modifier_type: number;
  min_answer: number;
  max_answer: number;
  is_all: boolean;
  is_device: boolean;
  is_web: boolean;
  display_order: number;
  is_active: boolean;
}

export interface CreateProductModifierMobileProps {
  language: any;
  isNew: boolean;
  formValues: ModifierFormValues;
  setFormValues: React.Dispatch<React.SetStateAction<ModifierFormValues>>;
  answers: MobileAnswerRow[];
  setAnswers: React.Dispatch<React.SetStateAction<MobileAnswerRow[]>>;
  /** Selected product ids for the "Applicable products" section (Extras only). */
  products: { product_id: string }[];
  listProducts: ProductOption[];
  onToggleProduct: (product: ProductOption) => void;
  productSearchValue: string;
  onProductSearchChange: (value: string) => void;
  loadingProducts: boolean;
  hasMoreProducts: boolean;
  onLoadMoreProducts: () => void;
  /** Option picker (Extras): the server-searched product options. */
  productOptions: ProductOption[];
  productOptionsLoading: boolean;
  productOptionsHasMore: boolean;
  onProductOptionsSearch: (value: string) => Promise<void> | void;
  onProductOptionsLoadMore: () => Promise<void> | void;
  onSave: () => Promise<void> | void;
  onLeave: () => void;
  /** True once the loaded record differs from what is on screen (guards Back / Cancel). */
  dirty: boolean;
  /** Field errors from the last save (server or limit check), keyed by API property. */
  errors?: Record<string, string>;
}

const MODIFIER_TYPE_LABELS: { value: number; label: string }[] = [
  { value: 1, label: 'Extras' },
  { value: 2, label: 'Services' },
  { value: 3, label: 'Restrictions' },
];

const INPUT: React.CSSProperties = {
  width: '100%',
  height: '48px',
  fontSize: '16px',
  lineHeight: '24px',
  color: TEXT,
  padding: '0 14px',
  border: `1px solid ${BORDER}`,
  borderRadius: '12px',
  backgroundColor: '#FFFFFF',
  outline: 'none',
};

const LABEL: React.CSSProperties = {
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 500,
  color: TEXT_MUTED,
  display: 'block',
  marginBottom: '6px',
};

const ERROR: React.CSSProperties = {
  fontSize: '13px',
  lineHeight: '18px',
  color: DESTRUCTIVE,
  marginTop: '6px',
};

const digitsOnly = (value: string): number => {
  const n = Number(String(value).replace(/[^\d]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const productLabel = (p: ProductOption | undefined | null, fallback: string): string =>
  p ? p.productName : fallback;

// Space on a div checkbox clicks it once, as on a native checkbox; Enter is left to the form.
const clickOnSpace = (e: React.KeyboardEvent<HTMLElement>) => {
  if (e.key !== ' ' || e.ctrlKey || e.metaKey || e.altKey) return;
  e.preventDefault();
  if (!e.repeat) e.currentTarget.click();
};

/** Bring the field with `id` into view (after the section that holds it has rendered). */
const revealField = (id: string) => {
  window.requestAnimationFrame(() => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    if (typeof el.focus === 'function') el.focus({ preventScroll: true });
  });
};

/* ------------------------------------------------------------------ */
/* Small pieces                                                        */
/* ------------------------------------------------------------------ */

const Field: React.FC<{
  label: string;
  required?: boolean;
  error?: string;
  htmlFor?: string;
  children: React.ReactNode;
}> = ({ label, required, error, htmlFor, children }) => (
  <div style={{ width: '100%' }}>
    <label className="font-inter" style={LABEL} htmlFor={htmlFor}>
      {label}
      {required && <span style={{ color: DESTRUCTIVE }}> *</span>}
    </label>
    {children}
    {error && (
      <p className="font-inter" style={ERROR}>
        {error}
      </p>
    )}
  </div>
);

const NumberInput: React.FC<{
  id?: string;
  value: number;
  onChange: (value: number) => void;
  invalid?: boolean;
}> = ({ id, value, onChange, invalid }) => (
  <input
    id={id}
    type="text"
    inputMode="numeric"
    pattern="[0-9]*"
    className="font-inter"
    style={{ ...INPUT, borderColor: invalid ? DESTRUCTIVE : BORDER }}
    value={String(value)}
    onFocus={e => e.target.select()}
    onChange={e => onChange(digitsOnly(e.target.value))}
  />
);

/** 44px pill button. `kind` picks filled teal / outline / destructive-outline. */
const PillButton: React.FC<{
  label: string;
  onClick: () => unknown;
  kind?: 'primary' | 'outline' | 'danger';
  disabled?: boolean;
  grow?: boolean;
  full?: boolean;
  /** kb.primaryProps of the enclosing form scope: Enter there presses this button. */
  primaryProps?: ReturnType<typeof useFormKeyboard>['primaryProps'];
}> = ({ label, onClick, kind = 'outline', disabled, grow, full, primaryProps }) => {
  const [pressed, setPressed] = useState(false);
  const press = () => (disabled ? undefined : onClick());
  const styles: React.CSSProperties =
    kind === 'primary'
      ? {
          backgroundColor: disabled ? '#9CC6C7' : pressed ? TEAL_DARK : TEAL,
          color: '#FFFFFF',
          border: '1px solid transparent',
        }
      : kind === 'danger'
        ? { backgroundColor: '#FFFFFF', color: DESTRUCTIVE, border: `1px solid ${DESTRUCTIVE}` }
        : { backgroundColor: '#FFFFFF', color: TEXT, border: `1px solid ${BORDER_STRONG}` };
  return (
    <button
      type="button"
      className="font-inter"
      disabled={disabled}
      aria-disabled={disabled}
      {...(primaryProps ? primaryProps(press) : { onClick: press })}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      style={{
        height: '44px',
        minWidth: '44px',
        padding: '0 20px',
        borderRadius: '30px',
        fontSize: '15px',
        fontWeight: 500,
        cursor: disabled ? 'default' : 'pointer',
        flex: grow ? 1 : undefined,
        width: full ? '100%' : undefined,
        ...styles,
      }}
    >
      {label}
    </button>
  );
};

/** Confirmation sheet: names the thing, offers the destructive / discard action and Cancel. */
const ConfirmSheet: React.FC<{
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  danger?: boolean;
  /** Confirms stack above the option sheet (200) and the product picker (220). */
  zIndex?: number;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ open, title, message, confirmLabel, cancelLabel, danger, zIndex = 230, onConfirm, onCancel }) => (
  <BottomSheet open={open} onClose={onCancel} title={title} zIndex={zIndex}>
    <p className="font-inter" style={{ padding: '0 20px 16px', fontSize: '15px', lineHeight: '22px', color: TEXT_MUTED }}>
      {message}
    </p>
    <div className="flex" style={{ gap: '12px', padding: '0 20px', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <PillButton label={cancelLabel} onClick={onCancel} grow />
      <PillButton label={confirmLabel} onClick={onConfirm} kind={danger ? 'danger' : 'primary'} grow />
    </div>
  </BottomSheet>
);

/** Collapsible section header: 48px band in the "Applies to" style, chevron on the right. */
const SectionHeader: React.FC<{
  title: string;
  summary?: string;
  open: boolean;
  onToggle: () => void;
}> = ({ title, summary, open, onToggle }) => (
  <button
    type="button"
    className="font-inter flex items-center justify-between"
    aria-expanded={open}
    onClick={onToggle}
    style={{
      width: '100%',
      minHeight: '48px',
      padding: '8px 16px',
      backgroundColor: '#EBF0F0',
      borderRadius: '12px',
      border: 'none',
      textAlign: 'left',
      cursor: 'pointer',
      gap: '12px',
    }}
  >
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontSize: '15px', fontWeight: 500, color: TEXT, lineHeight: '20px' }}>{title}</div>
      {!open && summary && (
        <div
          style={{
            fontSize: '13px',
            color: TEXT_DIM,
            lineHeight: '18px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {summary}
        </div>
      )}
    </div>
    <span style={{ display: 'inline-flex', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
      <Icon name="chevd" color={TEXT_MUTED} size={20} />
    </span>
  </button>
);

const CheckRow: React.FC<{ label: string; checked: boolean; onToggle: () => void; last?: boolean }> = ({
  label,
  checked,
  onToggle,
  last,
}) => (
  <div
    role="checkbox"
    aria-checked={checked}
    tabIndex={0}
    onKeyDown={clickOnSpace}
    className="flex items-center justify-between cursor-pointer"
    style={{ minHeight: '48px', padding: '12px 4px', borderBottom: last ? 'none' : `1px solid ${BORDER_SOFT}` }}
    onClick={onToggle}
  >
    <span className="font-inter" style={{ fontSize: '15px', fontWeight: 500, color: TEXT }}>
      {label}
    </span>
    <SelectBox checked={checked} />
  </div>
);

/* ------------------------------------------------------------------ */
/* Product picker sheet (full height, pinned search, 48px rows)        */
/* ------------------------------------------------------------------ */

const ProductPickerSheet: React.FC<{
  open: boolean;
  language: any;
  options: ProductOption[];
  loading: boolean;
  hasMore: boolean;
  selectedId: string | null;
  onSearch: (value: string) => Promise<void> | void;
  onLoadMore: () => Promise<void> | void;
  onPick: (product: ProductOption) => void;
  onClose: () => void;
}> = ({ open, language, options, loading, hasMore, selectedId, onSearch, onLoadMore, onPick, onClose }) => {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const lastSent = useRef('');
  // Escape is handled below; this gives focus back to the opener on close.
  useDialogFocus(open, dialogRef);

  // On every open: clear the box AND re-fetch the unfiltered first page, so a
  // previous search's rows never sit under an empty search field.
  // The picker is not a kit BottomSheet, so the option sheet beneath would
  // treat Escape as its own: catch it first (capture) and close only the picker.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // The option sheet beneath traps Tab in its own panel: cycle Close <-> search here instead.
      if (e.key === 'Tab') {
        e.stopImmediatePropagation();
        e.preventDefault();
        (document.activeElement === inputRef.current ? closeRef.current : inputRef.current)?.focus();
        return;
      }
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      e.preventDefault();
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(-1);
    lastSent.current = '';
    void onSearch('');
    const id = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(() => {
      if (query === lastSent.current) return;
      lastSent.current = query;
      void onSearch(query);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [open, onSearch, query]);

  useEffect(() => {
    if (active >= 0) listRef.current?.querySelectorAll('[role="option"]')[active]?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, backgroundColor: '#22252552', zIndex: 210 }} onClick={onClose} />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t(language, 'Select product', 'Select product')}
        className="flex flex-col"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          top: '8vh',
          backgroundColor: '#FFFFFF',
          borderRadius: '22px 22px 0 0',
          boxShadow: '0 -12px 40px rgba(0,0,0,.12)',
          zIndex: 220,
          overflow: 'hidden',
        }}
      >
        <div className="flex justify-center" style={{ padding: '10px 0 6px', flex: 'none' }}>
          <div style={{ width: '40px', height: '4px', borderRadius: '999px', backgroundColor: BORDER }} />
        </div>
        <div className="flex items-center" style={{ padding: '4px 20px 10px', gap: '8px', flex: 'none' }}>
          <div className="font-inter" style={{ flex: 1, fontSize: '18px', fontWeight: 600, color: TEXT }}>
            {t(language, 'Select product', 'Select product')}
          </div>
          <button
            ref={closeRef}
            type="button"
            aria-label={t(language, 'Close', 'Close')}
            onClick={onClose}
            className="flex items-center justify-center"
            style={{ width: '44px', height: '44px', border: 'none', background: 'transparent', borderRadius: '22px' }}
          >
            <Icon name="close" color={TEXT_MUTED} />
          </button>
        </div>
        <div style={{ padding: '0 16px 10px', flex: 'none', position: 'relative' }}>
          <span style={{ position: 'absolute', left: '30px', top: '14px', pointerEvents: 'none' }}>
            <Icon name="search" color={TEXT_DIM} />
          </span>
          <input
            ref={inputRef}
            type="search"
            inputMode="search"
            aria-label={t(language, 'Search product', 'Search product')}
            placeholder={t(language, 'Search product', 'Search product')}
            className="font-inter"
            style={{ ...INPUT, paddingLeft: '44px' }}
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setActive(-1);
            }}
            onKeyDown={e => {
              // Arrows move the highlight, Enter picks it.
              const { key } = e;
              if (key === 'ArrowDown' || key === 'ArrowUp') {
                e.preventDefault();
                setActive(i => Math.max(0, Math.min(options.length - 1, i + (key === 'ArrowDown' ? 1 : -1))));
              } else if (key === 'Enter') {
                e.preventDefault();
                if (options[active]) onPick(options[active]);
              }
            }}
          />
        </div>
        <div
          ref={listRef}
          role="listbox"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 4px env(safe-area-inset-bottom)' }}
          onScroll={e => {
            const el = e.currentTarget;
            if (!loading && hasMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
              void onLoadMore();
            }
          }}
        >
          {options.length === 0 && !loading && (
            <p className="font-inter" style={{ padding: '24px 16px', fontSize: '14px', color: TEXT_DIM }}>
              {t(language, 'No Results Found', 'No Results Found')}
            </p>
          )}
          {options.map((p, index) => {
            const selected = p.id === selectedId;
            return (
              <div
                key={p.id}
                role="option"
                aria-selected={selected}
                className="flex items-center cursor-pointer"
                onClick={() => onPick(p)}
                style={{
                  minHeight: '48px',
                  padding: '6px 16px',
                  gap: '12px',
                  borderRadius: '12px',
                  backgroundColor: selected ? '#E7F1F1' : 'transparent',
                  outline: index === active ? `2px solid ${TEAL}` : undefined,
                  outlineOffset: '-2px',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    className="font-inter"
                    style={{
                      fontSize: '15px',
                      fontWeight: 500,
                      color: TEXT,
                      lineHeight: '20px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.productName}
                  </div>
                  {p.sku && (
                    <div className="font-inter" style={{ fontSize: '13px', color: TEXT_DIM, lineHeight: '16px' }}>
                      {p.sku}
                    </div>
                  )}
                </div>
                {selected && <Icon name="check" color={TEAL} />}
              </div>
            );
          })}
          {loading && (
            <div className="flex items-center justify-center" style={{ padding: '16px' }}>
              <span className="loader" />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Option sheet (add / edit one answer)                                */
/* ------------------------------------------------------------------ */

interface OptionDraft {
  answer_text: string;
  product_id: string | null;
  product?: ProductOption | null;
  max_answer: number;
  display_order: number;
}

const OptionSheet: React.FC<{
  open: boolean;
  language: any;
  isExtras: boolean;
  editing: boolean;
  draft: OptionDraft;
  setDraft: React.Dispatch<React.SetStateAction<OptionDraft>>;
  onOpenPicker: () => void;
  onSave: () => void;
  onRemove: () => void;
  onClose: () => void;
}> = ({ open, language, isExtras, editing, draft, setDraft, onOpenPicker, onSave, onRemove, onClose }) => {
  const sheetKb = useFormKeyboard();
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (open) setTouched(false);
  }, [open]);

  const missing = isExtras ? !draft.product_id : !draft.answer_text.trim();
  const valid = !missing;
  const missingMsg = isExtras
    ? t(language, 'Choose a product', 'Choose a product')
    : t(language, 'Answer is required', 'Answer is required');
  const missingFieldId = isExtras ? 'pm-option-product' : 'pm-option-answer';

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={editing ? t(language, 'Edit option', 'Edit option') : t(language, 'Add option', 'Add option')}
    >
      <div {...sheetKb.scopeProps}>
      <div className="flex flex-col" style={{ padding: '0 20px', gap: '16px' }}>
        {isExtras ? (
          <Field
            label={t(language, 'Product', 'Product')}
            required
            htmlFor="pm-option-product"
            error={touched && missing ? missingMsg : undefined}
          >
            <button
              id="pm-option-product"
              type="button"
              onClick={onOpenPicker}
              aria-label={t(language, 'Select product', 'Select product')}
              aria-haspopup="listbox"
              className="font-inter flex items-center"
              style={{
                ...INPUT,
                textAlign: 'left',
                gap: '8px',
                cursor: 'pointer',
                borderColor: touched && missing ? DESTRUCTIVE : BORDER,
              }}
            >
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: draft.product_id ? TEXT : TEXT_DIM,
                }}
              >
                {draft.product_id
                  ? productLabel(draft.product, draft.answer_text)
                  : t(language, 'Select product', 'Select product')}
              </span>
              <Icon name="chevd" color={TEXT_MUTED} />
            </button>
            {draft.product_id && draft.product?.sku && (
              <p className="font-inter" style={{ fontSize: '13px', color: TEXT_DIM, marginTop: '4px' }}>
                {draft.product.sku}
              </p>
            )}
          </Field>
        ) : (
          <Field
            label={t(language, 'Answer', 'Answer')}
            required
            htmlFor="pm-option-answer"
            error={touched && missing ? missingMsg : undefined}
          >
            <input
              id="pm-option-answer"
              type="text"
              className="font-inter"
              style={{ ...INPUT, borderColor: touched && missing ? DESTRUCTIVE : BORDER }}
              value={draft.answer_text}
              placeholder={t(language, 'e.g. Rare, Medium Rare', 'e.g. Rare, Medium Rare')}
              onChange={e => setDraft(d => ({ ...d, answer_text: e.target.value }))}
              onBlur={() => setTouched(true)}
            />
          </Field>
        )}
        <div className="flex" style={{ gap: '12px' }}>
          {isExtras && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <Field label={t(language, 'Max answer', 'Max answer')} required htmlFor="pm-option-max">
                <NumberInput
                  id="pm-option-max"
                  value={draft.max_answer}
                  onChange={v => setDraft(d => ({ ...d, max_answer: v }))}
                />
              </Field>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Field label={t(language, 'Display order', 'Display order')} htmlFor="pm-option-order">
              <NumberInput
                id="pm-option-order"
                value={draft.display_order}
                onChange={v => setDraft(d => ({ ...d, display_order: v }))}
              />
            </Field>
          </div>
        </div>
        {editing && (
          <button
            type="button"
            onClick={onRemove}
            className="font-inter flex items-center"
            style={{
              minHeight: '44px',
              padding: '0 4px',
              border: 'none',
              background: 'transparent',
              color: DESTRUCTIVE,
              fontSize: '15px',
              fontWeight: 500,
              gap: '10px',
              cursor: 'pointer',
            }}
          >
            <Icon name="close" color={DESTRUCTIVE} />
            {t(language, 'Remove option', 'Remove option')}
          </button>
        )}
        <div
          className="flex"
          style={{ gap: '12px', paddingTop: '4px', paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <PillButton label={t(language, 'Cancel', 'Cancel')} onClick={onClose} grow />
          <PillButton
            label={editing ? t(language, 'Save option', 'Save option') : t(language, 'Add option', 'Add option')}
            kind="primary"
            grow
            primaryProps={sheetKb.primaryProps}
            onClick={() => {
              setTouched(true);
              if (valid) onSave();
              else revealField(missingFieldId);
            }}
          />
        </div>
      </div>
      </div>
    </BottomSheet>
  );
};

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

const CreateProductModifierMobile: React.FC<CreateProductModifierMobileProps> = ({
  language,
  isNew,
  formValues,
  setFormValues,
  answers,
  setAnswers,
  products,
  listProducts,
  onToggleProduct,
  productSearchValue,
  onProductSearchChange,
  loadingProducts,
  hasMoreProducts,
  onLoadMoreProducts,
  productOptions,
  productOptionsLoading,
  productOptionsHasMore,
  onProductOptionsSearch,
  onProductOptionsLoadMore,
  onSave,
  onLeave,
  dirty,
  errors,
}) => {
  const isExtras = formValues.modifier_type === 1;
  const limits = useFieldLimits(inventoryFormLimits('product-modifier'));
  const kb = useFormKeyboard();

  const [detailsOpen, setDetailsOpen] = useState(isNew);
  const [optionsOpen, setOptionsOpen] = useState(true);
  const [productsOpen, setProductsOpen] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [leaveConfirm, setLeaveConfirm] = useState(false);

  // A save error on a Details field opens Details and scrolls to it.
  useEffect(() => {
    const ids: Record<string, string> = { name: 'pm-name', min_answer: 'pm-min', max_answer: 'pm-max', display_order: 'pm-order' };
    const first = Object.keys(ids).find(key => errors?.[key]);
    if (!first) return;
    setDetailsOpen(true);
    window.setTimeout(() => revealField(ids[first]), 0);
  }, [errors]);

  // Edit mode: sections start collapsed-with-summary once the record has loaded.
  const seededRef = useRef(isNew);
  useEffect(() => {
    if (seededRef.current) return;
    if (formValues.name || answers.length > 0) {
      seededRef.current = true;
      setDetailsOpen(false);
      setOptionsOpen(true);
      setProductsOpen(false);
    }
  }, [answers.length, formValues.name]);

  /* ---- option sheet state ---- */
  const [optionSheet, setOptionSheet] = useState<{ open: boolean; index: number | null }>({ open: false, index: null });
  const [draft, setDraft] = useState<OptionDraft>({
    answer_text: '',
    product_id: null,
    product: null,
    max_answer: 1,
    display_order: 0,
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(false);

  const productById = useMemo(() => {
    const map = new Map<string, ProductOption>();
    productOptions.forEach(p => map.set(p.id, p));
    listProducts.forEach(p => {
      if (!map.has(p.id)) map.set(p.id, p);
    });
    return map;
  }, [listProducts, productOptions]);

  /** Answers in display order, each carrying its index in the parent's array. */
  const sortedAnswers = useMemo(
    () =>
      answers
        .map((a, index) => ({ a, index }))
        .sort((x, y) => x.a.display_order - y.a.display_order || x.index - y.index),
    [answers]
  );

  const openNewOption = () => {
    const nextOrder = answers.length === 0 ? 0 : Math.max(...answers.map(a => a.display_order)) + 1;
    setDraft({ answer_text: '', product_id: null, product: null, max_answer: 1, display_order: nextOrder });
    setOptionSheet({ open: true, index: null });
  };

  const openExistingOption = (index: number) => {
    const a = answers[index];
    setDraft({
      answer_text: a.answer_text,
      product_id: a.product_id,
      product: a.product_id ? productById.get(a.product_id) ?? null : null,
      max_answer: a.max_answer ?? 1,
      display_order: a.display_order,
    });
    setOptionSheet({ open: true, index });
  };

  const closeOptionSheet = () => {
    setOptionSheet({ open: false, index: null });
    setPickerOpen(false);
    setRemoveConfirm(false);
  };

  const saveOption = () => {
    const row: MobileAnswerRow = isExtras
      ? {
          answer_text: draft.product ? draft.product.productName : draft.answer_text,
          product_id: draft.product_id,
          color_code: null,
          display_order: draft.display_order,
          max_answer: draft.max_answer,
          is_active: true,
        }
      : {
          answer_text: draft.answer_text.trim(),
          product_id: null,
          color_code: null,
          display_order: draft.display_order,
          is_active: true,
        };
    // Same as desktop: a new option is appended, an edit touches only its own
    // row. Nobody else's display_order changes; the list sorts itself.
    setAnswers(prev => {
      if (optionSheet.index === null) return [...prev, { ...row, date: new Date() }];
      return prev.map((a, i) => (i === optionSheet.index ? { ...a, ...row } : a));
    });
    closeOptionSheet();
  };

  const removeOption = () => {
    if (optionSheet.index === null) return;
    const index = optionSheet.index;
    setAnswers(prev => prev.filter((_, i) => i !== index));
    closeOptionSheet();
  };

  /** Move the option at sorted position `pos` one step: the two rows that trade places swap display_order, nothing else changes. */
  const moveOption = (pos: number, delta: -1 | 1) => {
    const target = pos + delta;
    if (target < 0 || target >= sortedAnswers.length) return;
    const aIndex = sortedAnswers[pos].index;
    const bIndex = sortedAnswers[target].index;
    setAnswers(prev => {
      const aOrder = prev[aIndex].display_order;
      const bOrder = prev[bIndex].display_order;
      // Desktop commonly produces ties ([0, 0, 1, …]); swapping equal values
      // would be a no-op, so give the two rows distinct values in their new
      // order and leave every other row alone.
      const [movedOrder, otherOrder] =
        aOrder === bOrder ? (delta < 0 ? [aOrder, aOrder + 1] : [aOrder + 1, aOrder]) : [bOrder, aOrder];
      return prev.map((row, i) =>
        i === aIndex ? { ...row, display_order: movedOrder } : i === bIndex ? { ...row, display_order: otherOrder } : row
      );
    });
  };

  /* ---- validation ---- */
  const nameError = !formValues.name.trim()
    ? t(language, 'Name is required', 'Name is required')
    : validateAgainstLimits(formValues as any, limits, language, ['name']).name ?? '';
  const minError =
    formValues.min_answer < 0
      ? t(language, 'Min answers required', 'Min answers required')
      : formValues.min_answer > formValues.max_answer
        ? t(language, 'Minimum cannot exceed maximum', 'Minimum cannot exceed maximum')
        : '';
  const maxError = formValues.max_answer < 0 ? t(language, 'Max answers required', 'Max answers required') : '';
  const formValid = !nameError && !minError && !maxError;
  const showNameError = (nameTouched || saveAttempted) && nameError;

  // Save stays enabled: tapping it with invalid data marks the fields, opens
  // Details and scrolls the first offending field into view.
  const handleSave = () => {
    setSaveAttempted(true);
    setNameTouched(true);
    if (!formValid) {
      setDetailsOpen(true);
      revealField(nameError ? 'pm-name' : minError ? 'pm-min' : 'pm-max');
      return;
    }
    return onSave();
  };

  const requestLeave = () => {
    if (dirty) setLeaveConfirm(true);
    else onLeave();
  };

  /* ---- summaries ---- */
  const typeLabel = MODIFIER_TYPE_LABELS.find(x => x.value === formValues.modifier_type)?.label ?? '';
  const typeText = t(language, typeLabel, typeLabel);
  const nameText = formValues.name.trim() || t(language, 'No name yet', 'No name yet');
  const detailsSummary = [
    nameText,
    // Don't repeat the name when it is the type label ("Extras · Extras").
    nameText.toLowerCase() === typeText.toLowerCase() ? '' : typeText,
    `${formValues.min_answer}–${formValues.max_answer} ${t(language, 'answers', 'answers')}`,
  ]
    .filter(Boolean)
    .join(' · ');
  const selectedCount = products.length;
  const selectedIds = useMemo(() => new Set(products.map(p => p.product_id)), [products]);
  const orderedProducts = useMemo(() => {
    const assigned = listProducts.filter(p => selectedIds.has(p.id));
    const rest = listProducts.filter(p => !selectedIds.has(p.id));
    return [...assigned, ...rest];
  }, [listProducts, selectedIds]);

  const title = isNew ? t(language, 'Add modifier', 'Add modifier') : t(language, 'Edit modifier', 'Edit modifier');

  return (
    <div {...kb.scopeProps}>
    <div className="bg-white flex flex-col" style={{ height: '100%', minHeight: 0 }}>
      {/* Header: single Back + title, 48px */}
      <div className="flex items-center" style={{ height: '48px', flex: 'none', gap: '4px' }}>
        <button
          type="button"
          onClick={requestLeave}
          className="font-inter flex items-center"
          aria-label={t(language, 'Back', 'Back')}
          style={{
            height: '44px',
            minWidth: '44px',
            padding: '0 8px 0 0',
            border: 'none',
            background: 'transparent',
            gap: '2px',
            color: TEXT,
            fontSize: '15px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
            <Icon name="chev" color={TEXT} size={20} />
          </span>
          <span>{t(language, 'Back', 'Back')}</span>
        </button>
        <h1
          className="font-inter"
          style={{
            fontSize: '17px',
            fontWeight: 600,
            color: TEXT,
            margin: 0,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </h1>
      </div>

      {/* The page: the one vertical scroll surface */}
      <div
        className="scroll-container flex flex-col"
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', gap: '12px', paddingBottom: '16px' }}
        onScroll={e => {
          if (!isExtras || !productsOpen || loadingProducts || !hasMoreProducts) return;
          const el = e.currentTarget;
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 160) onLoadMoreProducts();
        }}
      >
        {/* ===== Details ===== */}
        <SectionHeader
          title={t(language, 'Details', 'Details')}
          summary={detailsSummary}
          open={detailsOpen}
          onToggle={() => setDetailsOpen(o => !o)}
        />
        {detailsOpen && (
          <div className="flex flex-col" style={{ gap: '16px', padding: '4px 0 8px' }}>
            <Field
              label={t(language, 'Modifier name', 'Modifier name')}
              required
              htmlFor="pm-name"
              error={showNameError ? nameError : errors?.name}
            >
              <input
                id="pm-name"
                type="text"
                name="name"
                className="font-inter"
                style={{ ...INPUT, borderColor: showNameError || errors?.name ? DESTRUCTIVE : BORDER }}
                value={formValues.name}
                placeholder={t(language, 'e.g. Choose milk', 'e.g. Choose milk')}
                maxLength={150}
                {...limitProps(limits, 'name')}
                onChange={e => setFormValues(prev => ({ ...prev, name: e.target.value }))}
                onBlur={() => setNameTouched(true)}
              />
              <FieldCounter value={formValues.name} max={limits.name?.maxLength} />
            </Field>

            <div>
              <span className="font-inter" style={LABEL}>
                {t(language, 'Modifier type', 'Modifier type')}
                <span style={{ color: DESTRUCTIVE }}> *</span>
              </span>
              <div role="radiogroup" className="flex" style={{ gap: '8px' }}>
                {MODIFIER_TYPE_LABELS.map(opt => {
                  const active = formValues.modifier_type === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      className="font-inter"
                      onClick={() => setFormValues(prev => ({ ...prev, modifier_type: opt.value }))}
                      style={{
                        flex: 1,
                        height: '44px',
                        borderRadius: '30px',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        border: `1px solid ${active ? TEAL : BORDER_STRONG}`,
                        backgroundColor: active ? TEAL : '#FFFFFF',
                        color: active ? '#FFFFFF' : TEXT,
                        padding: '0 8px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {t(language, opt.label, opt.label)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex" style={{ gap: '12px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Field label={t(language, 'Minimum answers', 'Minimum answers')} required htmlFor="pm-min" error={minError || errors?.min_answer || undefined}>
                  <NumberInput
                    id="pm-min"
                    invalid={!!(minError || errors?.min_answer)}
                    value={formValues.min_answer}
                    onChange={v => setFormValues(prev => ({ ...prev, min_answer: v }))}
                  />
                </Field>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Field label={t(language, 'Maximum answers', 'Maximum answers')} required htmlFor="pm-max" error={maxError || errors?.max_answer || undefined}>
                  <NumberInput
                    id="pm-max"
                    invalid={!!(maxError || errors?.max_answer)}
                    value={formValues.max_answer}
                    onChange={v => setFormValues(prev => ({ ...prev, max_answer: v }))}
                  />
                </Field>
              </div>
            </div>

            <Field label={t(language, 'Display order', 'Display order')} htmlFor="pm-order" error={errors?.display_order}>
              <NumberInput
                id="pm-order"
                invalid={!!errors?.display_order}
                value={formValues.display_order}
                onChange={v => setFormValues(prev => ({ ...prev, display_order: v }))}
              />
            </Field>

            <div>
              <div
                className="font-inter"
                style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: TEXT,
                  padding: '10px 16px',
                  backgroundColor: '#EBF0F0',
                  borderRadius: '12px',
                  marginBottom: '4px',
                }}
              >
                {t(language, 'Applies to:', 'Applies to:')}
              </div>
              <CheckRow
                label={t(language, 'All', 'All')}
                checked={formValues.is_all}
                onToggle={() => setFormValues(prev => ({ ...prev, is_all: !prev.is_all }))}
              />
              <CheckRow
                label={t(language, 'Device only', 'Device only')}
                checked={formValues.is_device}
                onToggle={() => setFormValues(prev => ({ ...prev, is_device: !prev.is_device }))}
              />
              <CheckRow
                label={t(language, 'Web', 'Web')}
                checked={formValues.is_web}
                onToggle={() => setFormValues(prev => ({ ...prev, is_web: !prev.is_web }))}
                last
              />
            </div>
          </div>
        )}

        {/* ===== Options ===== */}
        <SectionHeader
          title={`${t(language, 'Options', 'Options')} (${answers.length})`}
          summary={
            answers.length === 0
              ? t(language, 'No answer options added yet', 'No answer options added yet')
              : sortedAnswers
                  .slice(0, 3)
                  .map(s => s.a.answer_text || productLabel(productById.get(s.a.product_id ?? ''), '—'))
                  .join(' · ')
          }
          open={optionsOpen}
          onToggle={() => setOptionsOpen(o => !o)}
        />
        {optionsOpen && (
          <div className="flex flex-col" style={{ gap: '10px' }}>
            {sortedAnswers.length === 0 && (
              <p className="font-inter" style={{ fontSize: '14px', color: TEXT_DIM, padding: '4px 4px 0' }}>
                {t(language, 'No answer options added yet', 'No answer options added yet')}
              </p>
            )}
            {sortedAnswers.map(({ a, index }, pos) => {
              const product = a.product_id ? productById.get(a.product_id) : undefined;
              const name = isExtras ? productLabel(product, a.answer_text || '—') : a.answer_text || '—';
              const sku = isExtras ? product?.sku : undefined;
              return (
                <div
                  key={a.id ?? `row-${index}`}
                  className="flex items-stretch"
                  style={{ border: `1px solid ${BORDER}`, borderRadius: '12px', overflow: 'hidden' }}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    className="cursor-pointer"
                    onClick={() => openExistingOption(index)}
                    onKeyDown={e => {
                      if (e.key !== 'Enter' && e.key !== ' ') return;
                      e.preventDefault();
                      openExistingOption(index);
                    }}
                    style={{ flex: 1, minWidth: 0, padding: '12px 12px 12px 14px' }}
                  >
                    <div
                      className="font-inter"
                      style={{
                        fontSize: '15px',
                        fontWeight: 500,
                        color: TEXT,
                        lineHeight: '20px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {name}
                    </div>
                    {sku && (
                      <div className="font-inter" style={{ fontSize: '13px', color: TEXT_DIM, lineHeight: '18px' }}>
                        {sku}
                      </div>
                    )}
                    <div className="flex items-center" style={{ gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                      {isExtras && <Pill label={`${t(language, 'Max', 'Max')} ${a.max_answer ?? 1}`} kind="teal" />}
                      <Pill label={`${t(language, 'Order', 'Order')} ${a.display_order}`} kind="off" />
                    </div>
                  </div>
                  <div className="flex flex-col" style={{ flex: 'none', borderLeft: `1px solid ${BORDER_SOFT}` }}>
                    <button
                      type="button"
                      aria-label={t(language, 'Move up', 'Move up')}
                      disabled={pos === 0}
                      onClick={() => moveOption(pos, -1)}
                      className="flex items-center justify-center"
                      style={{
                        width: '44px',
                        flex: 1,
                        minHeight: '44px',
                        border: 'none',
                        background: 'transparent',
                        cursor: pos === 0 ? 'default' : 'pointer',
                        opacity: pos === 0 ? 0.3 : 1,
                      }}
                    >
                      <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
                        <Icon name="chevd" color={TEXT_MUTED} />
                      </span>
                    </button>
                    <button
                      type="button"
                      aria-label={t(language, 'Move down', 'Move down')}
                      disabled={pos === sortedAnswers.length - 1}
                      onClick={() => moveOption(pos, 1)}
                      className="flex items-center justify-center"
                      style={{
                        width: '44px',
                        flex: 1,
                        minHeight: '44px',
                        border: 'none',
                        background: 'transparent',
                        borderTop: `1px solid ${BORDER_SOFT}`,
                        cursor: pos === sortedAnswers.length - 1 ? 'default' : 'pointer',
                        opacity: pos === sortedAnswers.length - 1 ? 0.3 : 1,
                      }}
                    >
                      <Icon name="chevd" color={TEXT_MUTED} />
                    </button>
                  </div>
                </div>
              );
            })}
            <button
              type="button"
              onClick={openNewOption}
              className="font-inter flex items-center justify-center"
              style={{
                height: '44px',
                width: '100%',
                borderRadius: '30px',
                border: `1px solid ${TEAL}`,
                backgroundColor: '#FFFFFF',
                color: TEAL,
                fontSize: '15px',
                fontWeight: 500,
                gap: '6px',
                cursor: 'pointer',
              }}
            >
              <Icon name="plus" color={TEAL} />
              {t(language, 'Add option', 'Add option')}
            </button>
          </div>
        )}

        {/* ===== Products (Extras only) ===== */}
        {isExtras && (
          <>
            <SectionHeader
              title={`${t(language, 'Products', 'Products')} (${selectedCount})`}
              summary={
                selectedCount === 0
                  ? t(language, 'No products assigned', 'No products assigned')
                  : `${selectedCount} ${t(language, 'selected', 'selected')}`
              }
              open={productsOpen}
              onToggle={() => setProductsOpen(o => !o)}
            />
            {productsOpen && (
              <div className="flex flex-col" style={{ gap: '10px' }}>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '14px', top: '14px', pointerEvents: 'none' }}>
                    <Icon name="search" color={TEXT_DIM} />
                  </span>
                  <input
                    type="search"
                    inputMode="search"
                    aria-label={t(language, 'Search product', 'Search product')}
                    placeholder={t(language, 'Search product', 'Search product')}
                    className="font-inter"
                    style={{ ...INPUT, paddingLeft: '44px' }}
                    value={productSearchValue}
                    onChange={e => onProductSearchChange(e.target.value)}
                  />
                </div>
                {selectedCount > 0 && (
                  <div className="font-inter" style={{ fontSize: '13px', color: TEXT_DIM, padding: '0 4px' }}>
                    {t(language, 'Assigned products are listed first', 'Assigned products are listed first')}
                  </div>
                )}
                {orderedProducts.map(p => {
                  const checked = selectedIds.has(p.id);
                  return (
                    <div
                      key={p.id}
                      role="checkbox"
                      aria-checked={checked}
                      tabIndex={0}
                      onKeyDown={clickOnSpace}
                      className="flex items-center cursor-pointer"
                      onClick={() => onToggleProduct(p)}
                      style={{
                        minHeight: '56px',
                        padding: '10px 14px',
                        gap: '12px',
                        border: `1px solid ${checked ? TEAL : BORDER}`,
                        backgroundColor: checked ? '#F3F8F8' : '#FFFFFF',
                        borderRadius: '12px',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          className="font-inter"
                          style={{
                            fontSize: '15px',
                            fontWeight: 500,
                            color: TEXT,
                            lineHeight: '20px',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {p.productName}
                        </div>
                        <div
                          className="font-inter"
                          style={{
                            fontSize: '13px',
                            color: TEXT_DIM,
                            lineHeight: '18px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {[p.sku, p.productGroup?.groupName].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                      <SelectBox checked={checked} />
                    </div>
                  );
                })}
                {!loadingProducts && listProducts.length === 0 && (
                  <p className="font-inter" style={{ fontSize: '14px', color: TEXT_DIM, padding: '8px 4px' }}>
                    {t(language, 'No Results Found', 'No Results Found')}
                  </p>
                )}
                {loadingProducts && (
                  <div className="flex items-center justify-center" style={{ padding: '12px' }}>
                    <span className="loader" />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Sticky footer */}
      <div
        className="flex items-center shadow_button"
        style={{
          flex: 'none',
          gap: '12px',
          padding: '12px 0',
          paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
          borderTop: `1px solid ${BORDER}`,
          backgroundColor: '#FFFFFF',
          position: 'sticky',
          bottom: 0,
        }}
      >
        <PillButton label={t(language, 'Cancel', 'Cancel')} onClick={requestLeave} grow />
        <PillButton label={t(language, 'Save', 'Save')} onClick={handleSave} primaryProps={kb.primaryProps} kind="primary" grow />
      </div>

      {/* Sheets */}
      <OptionSheet
        open={optionSheet.open}
        language={language}
        isExtras={isExtras}
        editing={optionSheet.index !== null}
        draft={draft}
        setDraft={setDraft}
        onOpenPicker={() => setPickerOpen(true)}
        onSave={saveOption}
        onRemove={() => setRemoveConfirm(true)}
        onClose={closeOptionSheet}
      />
      <ProductPickerSheet
        open={pickerOpen}
        language={language}
        options={productOptions}
        loading={productOptionsLoading}
        hasMore={productOptionsHasMore}
        selectedId={draft.product_id}
        onSearch={onProductOptionsSearch}
        onLoadMore={onProductOptionsLoadMore}
        onPick={p => {
          setDraft(d => ({ ...d, product_id: p.id, product: p, answer_text: p.productName }));
          setPickerOpen(false);
        }}
        onClose={() => setPickerOpen(false)}
      />
      <ConfirmSheet
        open={removeConfirm}
        title={t(language, 'Remove option', 'Remove option')}
        message={`${t(language, 'Remove', 'Remove')} "${
          draft.product ? draft.product.productName : draft.answer_text || '—'
        }" ${t(language, 'from this modifier?', 'from this modifier?')}`}
        confirmLabel={t(language, 'Remove', 'Remove')}
        cancelLabel={t(language, 'Cancel', 'Cancel')}
        danger
        onConfirm={removeOption}
        onCancel={() => setRemoveConfirm(false)}
      />
      <ConfirmSheet
        open={leaveConfirm}
        title={t(language, 'Discard changes?', 'Discard changes?')}
        message={t(
          language,
          'You have unsaved changes to this modifier. Leaving now will discard them.',
          'You have unsaved changes to this modifier. Leaving now will discard them.'
        )}
        confirmLabel={t(language, 'Discard', 'Discard')}
        cancelLabel={t(language, 'Keep editing', 'Keep editing')}
        danger
        onConfirm={onLeave}
        onCancel={() => setLeaveConfirm(false)}
      />
    </div>
    </div>
  );
};

export default CreateProductModifierMobile;
